import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, Button, Typography } from "@mui/material";
import { TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useFormatResumoPisCofins } from "../../../hooks/useFormatResumoPisCofins";
import { useFormatResumoIcms } from "../../../pages/PreviaICMS/ModalAnalisar/useFormatResumoIcms";
import TableCardResumo from "../../TableCardResumo";
import {
  atualizarPreviaMotorResumo,
  atualizarProjetoMotorResumo,
} from "../../../services/api-planejamento-tributario.service";
import SnackBar from "../../SnackBar";
import ExportCsvButtonTributario from "../../ExportCsvButtonTributario";
import Loading from "../../Loading";
import ModalFiltrosComplexos from "../../ModalFiltrosComplexos";

const ResumoStepTable = ({
  resumo,
  idPreviaMotorResumo,
  idProjetoMotorResumo,
  loading = false,
  onComposicaoMudou,
  tipoPrevia = "PIS/COFINS", // "PIS/COFINS" ou "ICMS"
  isProjeto = false,
}) => {
  // Flag para ativar salvamento no backend
  const ENABLE_PERSISTENCE = true;

  // Selecionar hook baseado no tipo de prévia
  const useFormatHook =
    tipoPrevia === "ICMS" ? useFormatResumoIcms : useFormatResumoPisCofins;

  const { columns, formattedRows, updateCell, buildPayload, linhasOriginais } =
    useFormatHook(resumo);

  const [search, setSearch] = useState("");
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const [openModalFiltros, setOpenModalFiltros] = useState(false);
  const [filtrosComplexos, setFiltrosComplexos] = useState([]);
  const saveTimerRef = useRef(null);
  const isSavingRef = useRef(false);
  const pendingSaveRef = useRef(false);
  // Refs para evitar stale closures no agendamento/salvamento
  const buildPayloadRef = useRef(buildPayload);
  const idPreviaMotorResumoRef = useRef(idPreviaMotorResumo);
  const idProjetoMotorResumoRef = useRef(idProjetoMotorResumo);
  const descricaoRef = useRef(
    resumo?.descricao == null ? "" : String(resumo.descricao)
  );

  useEffect(() => {
    buildPayloadRef.current = buildPayload;
  }, [buildPayload]);

  useEffect(() => {
    idPreviaMotorResumoRef.current = idPreviaMotorResumo;
  }, [idPreviaMotorResumo]);

  useEffect(() => {
    idProjetoMotorResumoRef.current = idProjetoMotorResumo;
  }, [idProjetoMotorResumo]);

  useEffect(() => {
    descricaoRef.current =
      resumo?.descricao == null ? "" : String(resumo.descricao);
  }, [resumo]);

  // Função para aplicar filtros complexos
  const aplicarFiltroComplexo = useCallback(
    (row, filtro) => {
      const colunaSelecionada = columns.find((c) => c.label === filtro.coluna);
      if (!colunaSelecionada) {
        console.warn("🔍 [DEBUG FILTRO] Coluna não encontrada:", filtro.coluna);
        return true;
      }

      // 🚨 CORREÇÃO: Usar valor bruto em vez do formatado para comparações
      const valorRow = row.__raw
        ? row.__raw[`${colunaSelecionada.label}__${colunaSelecionada.tipo}`]
        : row[colunaSelecionada.label];
      const valorFiltro = filtro.valor;
      const valorFiltroFinal = filtro.valorFinal;

      // 🔍 DEBUG: Valores antes da conversão
      console.log("🔍 [DEBUG FILTRO] Valores antes da conversão:", {
        coluna: filtro.coluna,
        tipo: colunaSelecionada.tipo,
        valorRow: valorRow,
        valorFiltro: valorFiltro,
        valorFiltroFinal: valorFiltroFinal,
        operador: filtro.operador,
        rowIndex: row.__index,
      });

      // Helpers de data: parse seguro e normalização por dia
      const parseDateSafe = (valor) => {
        if (!valor) return null;
        if (valor instanceof Date) return new Date(valor.getTime());
        const str = String(valor);
        // Formato ISO curto YYYY-MM-DD
        if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
          const [y, m, d] = str.split("-").map((n) => parseInt(n, 10));
          // Construir como data local para não sofrer TZ
          return new Date(y, m - 1, d, 0, 0, 0, 0);
        }
        // Formato BR DD/MM/YYYY
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
          const [d, m, y] = str.split("/").map((n) => parseInt(n, 10));
          return new Date(y, m - 1, d, 0, 0, 0, 0);
        }
        // Fallback para Date parser (pode considerar TZ)
        const parsed = new Date(str);
        return isNaN(parsed.getTime()) ? null : parsed;
      };

      const startOfDay = (date) => {
        if (!date) return null;
        const d = new Date(date.getTime());
        d.setHours(0, 0, 0, 0);
        return d;
      };
      const endOfDay = (date) => {
        if (!date) return null;
        const d = new Date(date.getTime());
        d.setHours(23, 59, 59, 999);
        return d;
      };

      // Converter valores para comparação
      const converterValor = (valor, tipo) => {
        if (tipo === "date") {
          return parseDateSafe(valor);
        }
        if (tipo === "number" || tipo === "monetary" || tipo === "percent") {
          return Number(valor);
        }
        return String(valor).toLowerCase();
      };

      const valorConvertido = converterValor(valorRow, colunaSelecionada.tipo);
      const filtroConvertido = converterValor(
        valorFiltro,
        colunaSelecionada.tipo
      );
      const filtroFinalConvertido = valorFiltroFinal
        ? converterValor(valorFiltroFinal, colunaSelecionada.tipo)
        : null;

      // 🔍 DEBUG: Valores após conversão
      console.log("🔍 [DEBUG FILTRO] Valores após conversão:", {
        valorConvertido: valorConvertido,
        filtroConvertido: filtroConvertido,
        filtroFinalConvertido: filtroFinalConvertido,
        tipos: {
          valorConvertido: typeof valorConvertido,
          filtroConvertido: typeof filtroConvertido,
          filtroFinalConvertido: typeof filtroFinalConvertido,
        },
      });

      let resultado;
      switch (filtro.operador) {
        case "=":
          if (colunaSelecionada.tipo === "date") {
            // Igual ao dia: dentro do range do dia selecionado
            const rowDay = valorConvertido ? valorConvertido.getTime() : NaN;
            const dayStart = startOfDay(filtroConvertido)?.getTime();
            const dayEnd = endOfDay(filtroConvertido)?.getTime();
            resultado =
              !isNaN(rowDay) &&
              dayStart != null &&
              dayEnd != null &&
              rowDay >= dayStart &&
              rowDay <= dayEnd;
          } else {
            resultado = valorConvertido === filtroConvertido;
          }
          break;
        case "!=":
          if (colunaSelecionada.tipo === "date") {
            const rowDay = valorConvertido ? valorConvertido.getTime() : NaN;
            const dayStart = startOfDay(filtroConvertido)?.getTime();
            const dayEnd = endOfDay(filtroConvertido)?.getTime();
            const igualDia =
              !isNaN(rowDay) &&
              dayStart != null &&
              dayEnd != null &&
              rowDay >= dayStart &&
              rowDay <= dayEnd;
            resultado = !igualDia;
          } else {
            resultado = valorConvertido !== filtroConvertido;
          }
          break;
        case ">":
          if (colunaSelecionada.tipo === "date") {
            // Depois do fim do dia selecionado
            const rowTs = valorConvertido?.getTime();
            const cmpTs = endOfDay(filtroConvertido)?.getTime();
            resultado = rowTs != null && cmpTs != null && rowTs > cmpTs;
          } else {
            resultado = valorConvertido > filtroConvertido;
          }
          break;
        case "<":
          if (colunaSelecionada.tipo === "date") {
            // Antes do início do dia selecionado
            const rowTs = valorConvertido?.getTime();
            const cmpTs = startOfDay(filtroConvertido)?.getTime();
            resultado = rowTs != null && cmpTs != null && rowTs < cmpTs;
          } else {
            resultado = valorConvertido < filtroConvertido;
          }
          break;
        case ">=":
          if (colunaSelecionada.tipo === "date") {
            // A partir do início do dia
            const rowTs = valorConvertido?.getTime();
            const cmpTs = startOfDay(filtroConvertido)?.getTime();
            resultado = rowTs != null && cmpTs != null && rowTs >= cmpTs;
          } else {
            resultado = valorConvertido >= filtroConvertido;
          }
          break;
        case "<=":
          if (colunaSelecionada.tipo === "date") {
            // Até o fim do dia
            const rowTs = valorConvertido?.getTime();
            const cmpTs = endOfDay(filtroConvertido)?.getTime();
            resultado = rowTs != null && cmpTs != null && rowTs <= cmpTs;
          } else {
            resultado = valorConvertido <= filtroConvertido;
          }
          break;
        case "contains":
          // Para CNPJ, usar valor bruto (sem formatação)
          if (colunaSelecionada.tipo === "cnpj") {
            resultado = String(valorRow).includes(String(valorFiltro));
          } else {
            // Para outras strings, usar valor formatado para busca
            const valorFormatado = row[colunaSelecionada.label];
            resultado = String(valorFormatado)
              .toLowerCase()
              .includes(String(valorFiltro).toLowerCase());
          }
          break;
        case "startsWith":
          // Para CNPJ, usar valor bruto (sem formatação)
          if (colunaSelecionada.tipo === "cnpj") {
            resultado = String(valorRow).startsWith(String(valorFiltro));
          } else {
            const valorFormatadoStart = row[colunaSelecionada.label];
            resultado = String(valorFormatadoStart)
              .toLowerCase()
              .startsWith(String(valorFiltro).toLowerCase());
          }
          break;
        case "endsWith":
          // Para CNPJ, usar valor bruto (sem formatação)
          if (colunaSelecionada.tipo === "cnpj") {
            resultado = String(valorRow).endsWith(String(valorFiltro));
          } else {
            const valorFormatadoEnd = row[colunaSelecionada.label];
            resultado = String(valorFormatadoEnd)
              .toLowerCase()
              .endsWith(String(valorFiltro).toLowerCase());
          }
          break;
        case "between":
          if (colunaSelecionada.tipo === "date") {
            const rowTs = valorConvertido?.getTime();
            const startTs = startOfDay(filtroConvertido)?.getTime();
            const endTs = endOfDay(
              filtroFinalConvertido || filtroConvertido
            )?.getTime();
            resultado =
              rowTs != null &&
              startTs != null &&
              endTs != null &&
              rowTs >= startTs &&
              rowTs <= endTs;
          } else {
            resultado =
              valorConvertido >= filtroConvertido &&
              valorConvertido <= (filtroFinalConvertido ?? filtroConvertido);
          }
          break;
        default:
          resultado = true;
      }

      // 🔍 DEBUG: Resultado do filtro
      console.log("🔍 [DEBUG FILTRO] Resultado:", {
        operador: filtro.operador,
        resultado: resultado,
        expressao: `${valorConvertido} ${filtro.operador} ${filtroConvertido}`,
      });

      return resultado;
    },
    [columns]
  );

  const filteredRows = useMemo(() => {
    let filtered = formattedRows;

    // Aplicar filtro de busca
    if (search) {
      const lower = search.toLowerCase();
      console.log("🔍 [DEBUG BUSCA] Aplicando filtro de busca:", {
        termoBusca: search,
        termoBuscaLower: lower,
        totalLinhas: formattedRows.length,
      });

      filtered = filtered.filter((row) => {
        const valoresLinha = Object.values(row);
        const encontrou = valoresLinha.some((value) =>
          String(value).toLowerCase().includes(lower)
        );

        if (encontrou) {
          console.log("🔍 [DEBUG BUSCA] Linha encontrada:", {
            rowIndex: row.__index,
            valoresEncontrados: valoresLinha.filter((v) =>
              String(v).toLowerCase().includes(lower)
            ),
          });
        }

        return encontrou;
      });

      console.log("🔍 [DEBUG BUSCA] Resultado da busca:", {
        linhasEncontradas: filtered.length,
        linhasOriginais: formattedRows.length,
      });
    }

    // Aplicar filtros complexos
    if (filtrosComplexos.length > 0) {
      // Agrupar filtros por coluna para OR intra-coluna e AND inter-colunas
      const gruposPorColuna = filtrosComplexos.reduce((acc, f) => {
        if (!f || !f.coluna) return acc;
        if (!acc[f.coluna]) acc[f.coluna] = [];
        acc[f.coluna].push(f);
        return acc;
      }, {});

      console.log(
        "🔍 [DEBUG FILTROS COMPLEXOS] Aplicando filtros (AND entre colunas, OR dentro da mesma coluna):",
        {
          totalFiltros: filtrosComplexos.length,
          grupos: Object.entries(gruposPorColuna).map(([coluna, fs]) => ({
            coluna,
            qtde: fs.length,
            filtros: fs.map((f) => ({
              operador: f.operador,
              valor: f.valor,
              valorFinal: f.valorFinal,
            })),
          })),
          linhasAntes: filtered.length,
        }
      );

      filtered = filtered.filter((row) => {
        // Para cada coluna, pelo menos UM filtro do grupo deve passar (OR);
        // e TODAS as colunas (grupos) precisam passar (AND)
        const passouEmTodasColunas = Object.values(gruposPorColuna).every(
          (grupoFiltrosMesmaColuna) => {
            const passouAlgumDaColuna = grupoFiltrosMesmaColuna.some((filtro) =>
              aplicarFiltroComplexo(row, filtro)
            );

            console.log(
              "🔍 [DEBUG FILTROS COMPLEXOS] Linha",
              row.__index,
              "coluna",
              grupoFiltrosMesmaColuna?.[0]?.coluna,
              "passou algum=",
              passouAlgumDaColuna
            );
            return passouAlgumDaColuna;
          }
        );

        if (passouEmTodasColunas) {
          console.log(
            "🔍 [DEBUG FILTROS COMPLEXOS] Linha",
            row.__index,
            "aprovada por grupos (AND/OR)"
          );
        }

        return passouEmTodasColunas;
      });

      console.log(
        "🔍 [DEBUG FILTROS COMPLEXOS] Resultado final (grupos AND/OR):",
        {
          linhasFiltradas: filtered.length,
          linhasAntes: formattedRows.length,
        }
      );
    }

    // 🔍 LOGS: Estado dos resumos e filtros aplicados
    console.log("🔍 [ResumoStepTable] Estado dos resumos:", {
      dadosOriginais: formattedRows?.length || 0,
      dadosFiltrados: filtered?.length || 0,
      filtroBusca: search || "nenhum",
      filtrosComplexos: filtrosComplexos.length,
      detalhesFiltrosComplexos: filtrosComplexos.map((f) => ({
        coluna: f.coluna,
        operador: f.operador,
        valor: f.valor,
      })),
      percentualFiltrado:
        formattedRows?.length > 0
          ? Math.round((filtered?.length / formattedRows.length) * 100)
          : 0,
      action: "RESUMO_STATE_UPDATE",
    });

    return filtered;
  }, [formattedRows, search, filtrosComplexos, aplicarFiltroComplexo]);

  const handleCellChange = useCallback(
    (rowIndex, columnId, value) => {
      updateCell(rowIndex, columnId, value);
      if (ENABLE_PERSISTENCE) {
        scheduleSave();
      }
    },
    [updateCell]
  );

  const handleChangeBoolean = useCallback(
    (rowIndex, label, checked) => {
      updateCell(rowIndex, label, checked);
      if (ENABLE_PERSISTENCE) {
        // marca que há alterações pendentes; o efeito em linhasOriginais fará o debounce
        pendingSaveRef.current = true;
        if (saveTimerRef.current) {
          clearTimeout(saveTimerRef.current);
        }
        scheduleSave();
      }
    },
    [updateCell]
  );

  const scheduleSave = useCallback(() => {
    // Cancelar timer anterior se existir
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    // Agendar novo salvamento
    saveTimerRef.current = setTimeout(() => {
      performSave();
    }, 1000); // 1 segundo de delay
  }, []);

  const performSave = useCallback(async () => {
    if (isSavingRef.current) return; // Já salvando
    if (isProjeto) {
      if (!idProjetoMotorResumoRef.current) return;
    } else {
      if (!idPreviaMotorResumoRef.current) return;
    }

    isSavingRef.current = true;
    pendingSaveRef.current = false;

    try {
      const resumoPayload = buildPayloadRef.current();

      if (
        !resumoPayload ||
        !resumoPayload.dados ||
        resumoPayload.dados.length === 0
      ) {
        return;
      }

      // Nova estrutura da API: dados e colunas diretos no resumo
      const bodyResumo = {
        dados: resumoPayload.dados,
        colunas: resumoPayload.colunas,
      };

      if (isProjeto) {
        await atualizarProjetoMotorResumo(
          idProjetoMotorResumoRef.current,
          bodyResumo
        );
      } else {
        await atualizarPreviaMotorResumo(
          idPreviaMotorResumoRef.current,
          bodyResumo
        );
      }

      setObjSnackBar({
        type: "success",
        msg: "Alterações salvas com sucesso!",
      });

      // Notificar componente pai que a composição projeto mudou
      if (onComposicaoMudou) {
        const resumoAtualizado = {
          ...resumo,
          dados: bodyResumo.dados,
          colunas: bodyResumo.colunas,
        };

        const idResumo = isProjeto
          ? idProjetoMotorResumoRef.current
          : idPreviaMotorResumoRef.current;

        onComposicaoMudou(idResumo, resumoAtualizado);
      }
    } catch (error) {
      setObjSnackBar({
        type: "error",
        msg: "Erro ao salvar alterações",
      });
    } finally {
      isSavingRef.current = false;
    }
  }, [onComposicaoMudou, resumo]);

  // Dispara o salvamento após o commit de linhasOriginais, com debounce curto
  useEffect(() => {
    if (!ENABLE_PERSISTENCE) return;
    if (!pendingSaveRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      performSave();
    }, 50);
  }, [linhasOriginais, performSave]);

  // Cleanup do timer ao desmontar
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
      // flush final para não perder a última alteração ao desmontar
      if (ENABLE_PERSISTENCE && pendingSaveRef.current) {
        performSave();
      }
    };
  }, [performSave]);

  // Identificar a coluna "Composição Projeto" (boolean) para estilização condicional
  const normalize = (s) =>
    (s || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  const projetoCol = columns.find(
    (c) =>
      normalize(c.label).includes("composicao projeto") && c.tipo === "boolean"
  );

  // 🚀 FUNÇÕES PARA ALTERAÇÃO EM MASSA DA COMPOSIÇÃO PROJETO
  const marcarTodosProjeto = useCallback(() => {
    if (!projetoCol) {
      setObjSnackBar({
        type: "error",
        msg: "Coluna 'Composição Projeto' não encontrada!",
      });
      return;
    }

    // Atualizar apenas as linhas FILTRADAS (não todas)
    // Usar updateCell diretamente para cada linha filtrada
    filteredRows.forEach((row, index) => {
      const originalIndex = row.__index;
      updateCell(originalIndex, projetoCol.label, true);
    });

    // Disparar salvamento em lote após todas as alterações
    if (ENABLE_PERSISTENCE) {
      pendingSaveRef.current = true;
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
      scheduleSave();
    }
  }, [filteredRows, updateCell, projetoCol, scheduleSave]);

  const desmarcarTodosProjeto = useCallback(() => {
    if (!projetoCol) {
      setObjSnackBar({
        type: "error",
        msg: "Coluna 'Composição Projeto' não encontrada!",
      });
      return;
    }

    // Atualizar apenas as linhas FILTRADAS (não todas)
    // Usar updateCell diretamente para cada linha filtrada
    filteredRows.forEach((row, index) => {
      const originalIndex = row.__index;
      updateCell(originalIndex, projetoCol.label, false);
    });

    // Disparar salvamento em lote após todas as alterações
    if (ENABLE_PERSISTENCE) {
      pendingSaveRef.current = true;
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
      scheduleSave();
    }
  }, [filteredRows, updateCell, projetoCol, scheduleSave]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        <Loading loading={true} />
        <p style={{ marginTop: "1rem", color: "#666" }}>
          Carregando dados do resumo...
        </p>
      </div>
    );
  }

  if (!formattedRows || formattedRows.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
        Nenhum dado encontrado neste resumo.
      </div>
    );
  }

  const handleExportSuccess = () => {
    setObjSnackBar({
      type: "success",
      msg: "Arquivo exportado com sucesso!",
    });
  };

  const handleExportError = (message) => {
    setObjSnackBar({
      type: "error",
      msg: message,
    });
  };

  // Funções para gerenciar filtros complexos
  const handleAbrirModalFiltros = () => {
    setOpenModalFiltros(true);
  };

  const handleFecharModalFiltros = () => {
    setOpenModalFiltros(false);
  };

  const handleAplicarFiltrosComplexos = (novosFiltros) => {
    setFiltrosComplexos(novosFiltros);

    // 🔍 LOGS: Filtros sendo passados para a tabela
    console.log("🔍 [ResumoStepTable] Filtros aplicados na tabela:", {
      filtrosAnteriores: filtrosComplexos.length,
      filtrosNovos: novosFiltros.length,
      detalhesFiltros: novosFiltros.map((f) => ({
        coluna: f.coluna,
        operador: f.operador,
        valor: f.valor,
        valorFinal: f.valorFinal,
      })),
      timestamp: new Date().toISOString(),
      action: "FILTERS_APPLIED_TO_TABLE",
    });
  };

  const complementaryHeaderTop = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
        width: "100%",
      }}
    >
      {/* Linha 1: Busca e Botões */}
      <div
        style={{
          display: "flex",
          gap: "1rem",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        {/* Campo de busca simples */}
        <TextField
          size="small"
          label="Buscar por campos..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            minWidth: "250px",
            "& .MuiOutlinedInput-root": {
              fontSize: "0.8rem",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#18316e" }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Botões à direita */}
        <div
          style={{
            display: "flex",
            gap: "1rem",
            alignItems: "center",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={handleAbrirModalFiltros}
            size="small"
            sx={{
              borderColor: "#18316e",
              color: "#18316e",
              fontSize: "0.8rem",
              minWidth: "140px",
              transition: "transform 0.3s ease-in-out",
              "&:hover": {
                transform: "scale(1.05)",
                border: "1px solid #18316e",
              },
            }}
          >
            Filtros Avançados
          </Button>

          {resumo && (
            <ExportCsvButtonTributario
              data={filteredRows}
              columns={columns}
              filename={`resumo-${tipoPrevia.toLowerCase()}`}
              cnpj={resumo?.cnpj || ""}
              descricao={resumo?.descricao || ""}
              buttonLabel="Exportar Excel"
              onSuccess={handleExportSuccess}
              onError={handleExportError}
            />
          )}
        </div>
      </div>

      {/* Linha 2: Botões de Ação em Massa para Composição Projeto */}
      {projetoCol && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0.75rem 0",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#18316e",
              fontSize: "1rem",
            }}
          >
            Composição Projeto em Massa
          </Typography>

          <div
            style={{
              display: "flex",
              gap: "1rem",
              alignItems: "center",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={marcarTodosProjeto}
              sx={{
                borderColor: "#4caf50",
                color: "#4caf50",
                fontSize: "0.8rem",
                minWidth: "140px",
                transition: "transform 0.3s ease-in-out",
                "&:hover": {
                  transform: "scale(1.05)",
                  borderColor: "#388e3c",
                  backgroundColor: "rgba(76, 175, 80, 0.04)",
                },
              }}
            >
              ✅ Marcar Todos
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={desmarcarTodosProjeto}
              sx={{
                borderColor: "#f44336",
                color: "#f44336",
                fontSize: "0.8rem",
                minWidth: "140px",
                transition: "transform 0.3s ease-in-out",
                "&:hover": {
                  transform: "scale(1.05)",
                  borderColor: "#d32f2f",
                  backgroundColor: "rgba(244, 67, 54, 0.04)",
                },
              }}
            >
              ❌ Desmarcar Todos
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  const tableColumns = columns.map((c) => ({ key: c.label, label: c.label }));

  const data = (filteredRows || []).map((row) => {
    const clone = { ...row };
    // aplica cor vermelha na linha quando a composição de projeto estiver false
    if (projetoCol) {
      const keyRaw = `${projetoCol.label}__${projetoCol.tipo}`;
      const isProjeto = row.__raw ? row.__raw[keyRaw] : undefined;
      if (isProjeto === false) {
        clone.style = { ...(clone.style || {}), color: "#d32f2f" };
      } else if (isProjeto === true) {
        clone.style = { ...(clone.style || {}), color: "#000000" };
      }
    }
    columns.forEach((c) => {
      if (c.editable && c.tipo === "boolean") {
        const rawBool = !!row.__raw?.[`${c.label}__${c.tipo}`];
        clone[c.label] = (
          <Checkbox
            size="small"
            checked={rawBool}
            onChange={(e) =>
              handleChangeBoolean(row.__index, c.label, e.target.checked)
            }
            sx={{ p: 0.5 }}
          />
        );
      }
    });
    return clone;
  });

  return (
    <>
      <TableCardResumo
        columns={tableColumns}
        data={data}
        loading={loading}
        complementaryHeaderTop={complementaryHeaderTop}
      />

      <ModalFiltrosComplexos
        open={openModalFiltros}
        onClose={handleFecharModalFiltros}
        columns={columns}
        onApplyFilters={handleAplicarFiltrosComplexos}
        title="Filtros Avançados"
        context={tipoPrevia}
      />

      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
    </>
  );
};

export default ResumoStepTable;
